import {
  FocusEvent,
  KeyboardEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { ActionMeta, OnChangeValue } from 'react-select';

import {
  CategoryOption,
  RESPONSE_FIXED,
  RESPONSE_FREE,
  SettingProps,
} from '../interactive-response-categorise.constants';
import {
  SettingSmallText,
  SettingsGroup,
  SettingsHr,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { CreatableStyled } from 'lib/form/tag-autocomplete/tag-autocomplete.style';
import { SmallStyled } from 'approot/shared/styles/typography.style';
import { ENTER } from 'approot/shared/ally/keyboard.hooks';
import { useInteractiveResponseCategoriseUpdate } from './interactive-response-categorise.hooks';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import { ImageThumbnailStatic } from 'approot/shared/images/images';
import { BoxList, Box } from 'approot/shared/design-system/box/box.style';
import { HrStyled } from 'approot/shared/styles/decorative.style';
import {
  addCategory,
  adjustItemsForRemovedCategory,
  getCategoryFromOption,
  getShouldShowNumberOfItemsHint,
} from './interactive-response-categorise.utils';
import { grey7 } from 'approot/shared/styles/colours.style';
import {
  SortableContent,
  SortableItem,
} from 'approot/shared/sortable/sortable.style';
import Switch from 'approot/shared/switch/switch';
import IconExclamation from 'approot/shared/icons/icon-exclamation';
import { InteractiveResponseFeedbackSetting } from '../../shared/interactive-response-shared';
import { isTextVariant } from '../../shared/interactive-response-shared.utils';
import {
  InteractiveDisplayItem,
  InteractiveDragContainer,
} from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag.style';
import InteractiveResponseRichtextView from '../../shared/interactive-response-richtext-view';
import { InteractiveResponseQuestionSequenceItem } from '../../sequence/settings/interactive-response-settings.style';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { LESSON_ACTIVITIES } from 'global/constants';

type Props = SettingProps & {
  onCloseChildDrawer?: () => void;
};

const InteractiveResponseCategoriseSolution = (props: Props) => {
  const { properties } = props;
  const interactiveResponse = properties.interactiveResponseCategorise;

  const isFixedAnswerType =
    interactiveResponse.answerType == null ||
    interactiveResponse.answerType === RESPONSE_FIXED;

  const { contentType } = useGetInteractiveLessonParams();

  const items = useMemo(() => {
    // Use parent variant if not item variant is set
    return interactiveResponse.items.map(item => ({
      ...item,
      variant: item.variant || interactiveResponse.variant,
    }));
  }, [interactiveResponse]);

  const [options, setOptions] = useState<CategoryOption[]>(
    interactiveResponse.categories.map(c => ({
      label: c.label,
      value: c.id,
    })) || []
  );

  const hasEmptyGroup = useMemo(() => {
    for (let i = 0; i < options.length; i++) {
      if (!items.some(item => item.categoryId === options[i].value)) {
        return true;
      }
    }
    return false;
  }, [items, options]);

  const { handleUpdate } = useInteractiveResponseCategoriseUpdate(props);

  const handleOnChange = useCallback(
    (
      newCategories: OnChangeValue<CategoryOption, true>,
      { action }: ActionMeta<CategoryOption>
    ) => {
      if (
        (action === 'pop-value' || action === 'remove-value') &&
        Array.isArray(newCategories)
      ) {
        const categories = [...newCategories];
        setOptions(categories);
        handleUpdate({
          ...properties,
          interactiveResponseCategorise: {
            ...properties.interactiveResponseCategorise,
            items: adjustItemsForRemovedCategory(
              properties.interactiveResponseCategorise.categories,
              categories,
              properties.interactiveResponseCategorise.items
            ),
            categories: getCategoryFromOption(categories),
          },
          categoriseDimensionData: properties.categoriseDimensionData,
          style: properties.style,
          __typename: 'CategoriseResponseProperties',
        });
      }
    },
    [handleUpdate, properties]
  );

  const handleOnKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.key === ENTER) {
      event.preventDefault();
      (event.target as HTMLInputElement).blur();
      (event.target as HTMLInputElement).focus();
    }
  }, []);

  const handleOnBlur = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      const target = event.target as HTMLInputElement;

      if (target.value.length > 0) {
        event.preventDefault();
        setOptions(addCategory(target.value, options));
        const categories = getCategoryFromOption(options);
        handleUpdate({
          ...properties,
          interactiveResponseCategorise: {
            ...properties.interactiveResponseCategorise,
            items:
              categories.length === 1
                ? properties.interactiveResponseCategorise.items.map(item => ({
                    ...item,
                    categoryId: categories[0].id,
                  }))
                : properties.interactiveResponseCategorise.items,
            categories,
          },
          categoriseDimensionData: properties.categoriseDimensionData,
          style: properties.style,
          __typename: 'CategoriseResponseProperties',
        });
      }
    },
    [options, handleUpdate, properties]
  );

  return (
    <>
      <SettingsGroup>
        <SettingsLabel>Groups</SettingsLabel>
        <CreatableStyled
          classNamePrefix="Select"
          isMulti
          isClearable={false}
          menuIsOpen={false}
          value={options}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          onKeyDown={(e: KeyboardEvent<HTMLElement>) => handleOnKeyDown(e)}
        />
        <SettingSmallText colour={grey7} marginBottom={0}>
          Type and press enter to add groups
        </SettingSmallText>
      </SettingsGroup>
      <SettingsHr />
      <SettingsGroup>
        <SettingsLabel>Answer Type</SettingsLabel>
        <CheckableSingle
          name="answerType"
          selected={interactiveResponse.answerType}
          options={[
            {
              label: 'Fixed',
              value: RESPONSE_FIXED,
            },
            {
              label: 'Free',
              value: RESPONSE_FREE,
            },
          ]}
          onSelect={answerType =>
            handleUpdate({
              ...properties,
              interactiveResponseCategorise: {
                ...interactiveResponse,
                answerType,
                showNumberOfItemsHint: answerType === RESPONSE_FIXED,
              },
            })
          }
        />
        {interactiveResponse.answerType === RESPONSE_FIXED && (
          <SmallStyled marginBottom={0}>
            There is only one correct answer.
          </SmallStyled>
        )}
        {interactiveResponse.answerType === RESPONSE_FREE && (
          <SmallStyled marginBottom={0}>
            There is no predetermined answer and students are encouraged to
            provide their own responses based on their creativity and
            understanding.
          </SmallStyled>
        )}
      </SettingsGroup>
      {contentType === LESSON_ACTIVITIES && isFixedAnswerType && (
        <SettingsGroup>
          <SettingsLabel>Show number of items per group</SettingsLabel>
          <Switch
            ariaLabel="Show number of items per group"
            state={getShouldShowNumberOfItemsHint(
              properties.interactiveResponseCategorise.showNumberOfItemsHint,
              contentType
            )}
            onClick={() =>
              handleUpdate({
                ...properties,
                interactiveResponseCategorise: {
                  ...interactiveResponse,
                  showNumberOfItemsHint:
                    !interactiveResponse.showNumberOfItemsHint,
                },
              })
            }
          />
        </SettingsGroup>
      )}
      <SettingsHr />
      {contentType === LESSON_ACTIVITIES &&
        interactiveResponse.answerType === RESPONSE_FIXED && (
          <>
            <InteractiveResponseFeedbackSetting
              onSelect={checkAnswer =>
                handleUpdate({
                  ...properties,
                  interactiveResponseCategorise: {
                    ...interactiveResponse,
                    checkAnswerAtEnd: checkAnswer === 'end',
                  },
                })
              }
              isCheckAnswerAtEnd={
                interactiveResponse.checkAnswerAtEnd ||
                interactiveResponse.checkAnswerAtEnd === null
              }
            />

            <SettingsHr />
          </>
        )}

      {isFixedAnswerType && (
        <SettingsGroup>
          <SettingsLabel>Match items to the right group below</SettingsLabel>
          {hasEmptyGroup && (
            <Box $alignItems="center" $gap={4} $marginBottom={16}>
              <IconExclamation width={16} height={16} />
              <SettingSmallText marginBottom={0}>
                You have one or more empty groups.
              </SettingSmallText>
            </Box>
          )}
          <BoxList data-selector="categorise-items">
            {items.map(item => (
              <SortableItem
                as="li"
                key={item.itemId}
                data-selector="categorise-item-list"
              >
                <SortableContent>
                  {isTextVariant(item.variant) ? (
                    <InteractiveResponseQuestionSequenceItem>
                      <InteractiveDragContainer $scaleTo={54}>
                        <InteractiveDisplayItem $baseColour={item.baseColour}>
                          <InteractiveResponseRichtextView
                            baseColour={item.baseColour}
                            richtext={item.richtext}
                          />
                        </InteractiveDisplayItem>
                      </InteractiveDragContainer>
                    </InteractiveResponseQuestionSequenceItem>
                  ) : (
                    <ImageThumbnailStatic
                      imageUrl={item.image?.original || ''}
                    />
                  )}
                </SortableContent>
                <HrStyled $marginBottom={0} $marginTop={0} />
                <SortableContent>
                  <SettingsGroup marginBottom={0}>
                    {options.length === 0 ? (
                      <SmallStyled marginBottom={0} colour={grey7}>
                        No groups created
                      </SmallStyled>
                    ) : (
                      <CheckableSingle
                        name={`${item.itemId}`}
                        isSmall
                        marginBottom={0}
                        selected={item.categoryId || ''}
                        options={options.map(o => ({
                          label: o.label,
                          value: o.value,
                        }))}
                        onSelect={value => {
                          handleUpdate({
                            ...properties,
                            interactiveResponseCategorise: {
                              ...interactiveResponse,
                              items: interactiveResponse.items.map(i => {
                                if (i.itemId === item.itemId) {
                                  return {
                                    ...i,
                                    categoryId: value,
                                  };
                                }
                                return i;
                              }),
                            },
                          });
                        }}
                      />
                    )}
                  </SettingsGroup>
                </SortableContent>
              </SortableItem>
            ))}
          </BoxList>
        </SettingsGroup>
      )}
    </>
  );
};

export default InteractiveResponseCategoriseSolution;
